import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { NoChildDataNotice } from '../components/NoChildDataNotice';
import { TrendingUp, TrendingDown, Minus, Lightbulb, ArrowRight, Download, FileText, MoreVertical } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { getModuleIcon, type ModuleData } from '../../../content';
import { Skeleton } from '../../../components/shared/Skeleton';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { parseGroupNum } from '../shared/groupUtils';
import { downloadCsv } from '../../../utils/csvExport';
import { useGrowthData } from '../hooks/useGrowthData';
import { useModuleMastery } from '../hooks/useModuleMastery';
import { useLatestAssessment } from '../hooks/useLatestAssessment';
import { DataFreshness } from '../../shared/DataFreshness';
import { getChildLastActivity } from '../shared/childActivity';
import { useRefreshChild } from '../hooks/useRefreshChild';
import { ParentV2Route } from '../ParentSidebarV2';
import { SubjectModuleRow } from '../../shared/SubjectModuleRow';
import { SubjectsSummaryHero } from '../../shared/SubjectsSummaryHero';
import { computeSkillMasteries } from '../../../mastery';
import { getMasteryBand } from '../../shared/masteryBands';
interface VoortgangPageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
  onNavigate?: (route: ParentV2Route) => void;
}
type SortOption = 'attention' | 'strong' | 'alpha';
function ParentModuleExpandContent({
  module,
  childData,
  selectedGroup,
  mastery,
  selectedChild
}: {
  module: ModuleData;
  childData: AppState | null;
  selectedGroup: string;
  mastery: number;
  selectedChild: LearnerAccount;
}) {
  const groupNum = parseGroupNum(selectedGroup);
  const skillMasteries = useMemo(() => {
    if (!childData) return [];
    return computeSkillMasteries(module.id, groupNum, childData.questionStates || {}, childData.familyMastery);
  }, [module.id, groupNum, childData]);
  const suggestions = useMemo(() => {
    const name = selectedChild.displayName;
    if (mastery < 35) {
      return ['Korte sessies van 5-10 min, focus op één sub-vaardigheid tegelijk.', 'Vier elke kleine winst — motivatie is hier belangrijker dan tempo.'];
    }
    if (mastery < 75) {
      return ['Houd het ritme van 3-4 dagen per week vast.', 'Bekijk samen 1 foutje per sessie — vragen werken beter dan antwoorden geven.'];
    }
    return [`Goed bezig! Hier mag ${name} af en toe een uitdaging op een hoger niveau proberen.`, 'Doe een toets om het niveau te bevestigen.'];
  }, [mastery, selectedChild.displayName]);
  const humanizeSkill = (skill: string) => {
    const cleaned = skill.replace(/_/g, ' ').toLowerCase();
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };
  return <div className="p-4 sm:pl-18 space-y-6" data-id="element-8992" data-mp="1rci4joxh5lzj">
      {/* Sub-skills */}
      <section data-id="element-8993" data-mp="262xt9dp3nc2g">
        <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8994" data-mp="262pjhuo9wmvh">
          Wat oefent {selectedChild.displayName} hier?
        </h3>
        {skillMasteries.length > 0 ? <div className="space-y-2" data-id="element-8995" data-mp="1dfbnpzwb9xcy">
            {skillMasteries.map(skill => {
          const trendIcon = skill.trend === 'improving' ? <TrendingUp className="w-3 h-3" data-id="element-8996" data-mp="18i33zuvill9l" /> : skill.trend === 'struggling' ? <TrendingDown className="w-3 h-3" data-id="element-8997" data-mp="1nekgwqjcr7y9" /> : <Minus className="w-3 h-3" data-id="element-8998" data-mp="1idwi3iovtmth" />;
          const trendColor = skill.trend === 'improving' ? 'text-emerald-600 bg-emerald-50' : skill.trend === 'struggling' ? 'text-rose-600 bg-rose-50' : 'text-slate-500 bg-slate-100';
          return <div key={skill.skill} className="p-3 border border-slate-200 bg-white rounded-xl shadow-sm" data-id="element-8999" data-mp="2ttuy66ih9947">
                
                  <div className="flex items-center gap-3 mb-2" data-id="element-9000" data-mp="13divkimnntwf">
                    <div className="flex-1 min-w-0" data-id="element-9001" data-mp="18a9av3qmylgs">
                      <div className="text-sm font-bold text-slate-900 truncate" data-id="element-9002" data-mp="18d5acwi4ti7c">
                        {humanizeSkill(skill.skill)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0" data-id="element-9003" data-mp="1hd0l507rzb6u">
                      <span className="text-sm font-black text-slate-800" data-id="element-9004" data-mp="1ma0vhnbnr03r">
                        {skill.masteryScore}%
                      </span>
                      <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${trendColor}`} data-id="element-9005" data-mp="1w0lo8u4nhaer">
                      
                        {trendIcon}
                      </div>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-9006" data-mp="2534nz5d536ws">
                    <div className={`h-full rounded-full transition-all ${getMasteryBand(skill.masteryScore).barClass}`} style={{
                width: `${skill.masteryScore}%`
              }} data-id="element-9007" data-mp="2a0wga0hp4v1i" />
                  
                  </div>
                </div>;
        })}
          </div> : <div className="text-sm text-slate-500 italic" data-id="element-9008" data-mp="2a76wyk0r7k45">
            Nog geen sub-vaardigheden gemeten.
          </div>}
      </section>

      {/* Suggestions */}
      <section data-id="element-9009" data-mp="32ut94go1rx2m">
        <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-9010" data-mp="32vci37vpzb4i">
          <Lightbulb className="w-3 h-3" data-id="element-9011" data-mp="2xwrgp54qeleh" />
          Wat helpt thuis?
        </h3>
        <div className="p-3 border border-slate-200 bg-white rounded-xl shadow-sm" data-id="element-9012" data-mp="2t4ht3vr39xfd">
          <ul className="space-y-2" data-id="element-9013" data-mp="2p3o6r6taoqol">
            {suggestions.map((suggestion, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-9014" data-mp="2p1bqcy36qodn" data-mpi={i}>
              
                <span className="text-emerald-500 font-bold flex-shrink-0 mt-0.5" data-id="element-9015" data-mp="3lnf30vfe9dkk">
                  •
                </span>
                <span data-id="element-9016" data-mp="1exbrzyzfjtlz">{suggestion}</span>
              </li>)}
          </ul>
        </div>
      </section>
    </div>;
}
export function VoortgangPage({
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  getLearnerModuleMastery,
  setViewingLearner,
  onNavigate
}: VoortgangPageProps) {
  const {
    refresh
  } = useRefreshChild(selectedChild ? [selectedChild] : []);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('attention');
  const [showMenu, setShowMenu] = useState(false);
  const groupNum = parseGroupNum(selectedGroup);
  const {
    report: latestAssessmentReport,
    isLoading: isAssessmentLoading
  } = useLatestAssessment(selectedChild, selectedGroup);
  const growthData = useGrowthData(selectedChild, childData, selectedGroup, getLearnerModuleMastery);
  const moduleMasteryList = useModuleMastery(selectedChild, getLearnerModuleMastery);
  const handleExportCsv = () => {
    if (!selectedChild || !growthData) return;
    const headers = ['Vak', 'Beheersing (%)', 'Niveau', 'Trend', 'Trend (%)'];
    const rows = growthData.modules.map(m => {
      const trendText = m.trend === 'up' ? 'Stijgt' : m.trend === 'down' ? 'Daalt' : 'Stabiel';
      return [`"${m.module.title}"`, m.mastery, `"${getMasteryBand(m.mastery).label}"`, `"${trendText}"`, m.delta].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const safeName = selectedChild.displayName.replace(/[^a-zA-Z0-9]/g, '_');
    downloadCsv(csvContent, `voortgang_${safeName}`);
    setShowMenu(false);
  };
  const sortedModules = useMemo(() => {
    const list = [...moduleMasteryList];
    return list.sort((a, b) => {
      if (sortBy === 'attention') {
        return a.mastery - b.mastery;
      }
      if (sortBy === 'strong') {
        return b.mastery - a.mastery;
      }
      // alpha
      return a.module.title.localeCompare(b.module.title);
    });
  }, [moduleMasteryList, sortBy]);
  /*
   Twee toestanden die hier op één hoop lagen. `growthData` is null zodra er
   geen lokale oefengegevens zijn, en dat leverde "Nog geen kind gekoppeld" op
   terwijl het kind gewoon gekoppeld is. Voor een ouder die net de koppelcode
   heeft gebruikt, leest dat als "het is niet gelukt" — en dan gaat hij het
   nog een keer proberen met een code die inmiddels vervangen kan zijn.
   */
  if (selectedChild && !growthData) {
    return <PageLayout title="Vakken" description={`Bekijk de voortgang per vak van ${selectedChild.displayName}`} data-id="element-9017" data-mp="1uoclixpm0crp">
        <div className="py-12" data-id="element-9018" data-mp="1zme7bqef6q6v">
          <NoChildDataNotice childName={selectedChild.displayName} whatAppearsHere="Zodra er geoefend is, zie je hier per vak hoe het gaat." data-id="element-9019" data-mp="24l8pcq4kcapx" />
        </div>
      </PageLayout>;
  }
  if (!selectedChild) {
    return (
      // Hier stond "Geen data beschikbaar." Voor een ouder die net een account
      // heeft gemaakt leest dat als een storing, terwijl er simpelweg nog geen
      // kind gekoppeld is. Dat is geen fout maar een volgende stap, en dan
      // hoort er te staan wat die stap is.
      <PageLayout title="Vakken" description="Bekijk de voortgang per vak" data-id="element-9020" data-mp="14d271kdqjrks">
        <div className="text-center py-16 max-w-md mx-auto" data-id="element-9021" data-mp="14f57yzgff54a">
          <p className="text-slate-700 font-semibold mb-1" data-id="element-9022" data-mp="zlafb6iufft2">
            Nog geen kind gekoppeld
          </p>
          <p className="text-sm text-slate-500" data-id="element-9023" data-mp="umg00ek349dp">
            Zodra je een kind koppelt met de koppelcode uit zijn of haar
            profiel, zie je hier per vak hoe het gaat. Koppelen doe je bij
            Instellingen.
          </p>
        </div>
      </PageLayout>
    );
  }
  const topSubject = growthData.strengths[0]?.module.title;
  const growthSubject = growthData.areasForGrowth[0]?.module.title;
  return <PageLayout title="Vakken" description={`Bekijk de voortgang per vak van ${selectedChild.displayName}`} actions={<>
          <DataFreshness updatedAt={getChildLastActivity(childData)} onRefresh={refresh} data-id="element-9025" data-mp="2az4snfqa0953" />
        
          <div className="relative" data-id="element-9026" data-mp="260l5jbzy4rb5">
            <button onClick={() => setShowMenu(!showMenu)} aria-label="Meer opties" aria-haspopup="menu" aria-expanded={showMenu} className="flex items-center justify-center w-11 h-11 rounded-xl text-slate-500 hover:bg-slate-100 transition-all border border-transparent hover:border-slate-200" data-id="element-9027" data-mp="217g0z8hwhatj">
            
              <MoreVertical className="w-5 h-5" data-id="element-9028" data-mp="268dhlgn9hwsx" />
            </button>
            {showMenu && <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} data-id="element-9029" data-mp="1iisviovtajff" />
            
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 z-50 overflow-hidden" data-id="element-9030" data-mp="1djpq33bx6b6x">
                  <button onClick={handleExportCsv} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors text-left" data-id="element-9031" data-mp="1rc5zclvez5ga">
                
                    <Download className="w-4 h-4" data-id="element-9032" data-mp="yre446rxdv6h" />
                    Exporteer CSV
                  </button>
                </div>
              </>}
          </div>
        </>} data-id="element-9024" data-mp="bpy039v3136d">
      
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-9033" data-mp="kmxazw3328x7">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-9034" data-mp="pf6wmzx60mto" />
        

        <div className="space-y-8" data-id="element-9035" data-mp="arlwnevl73pz">
          {/* Summary Hero */}
          <SubjectsSummaryHero variant="parent" childName={selectedChild.displayName} overallMastery={growthData.overallMastery} groupNum={groupNum} topSubject={topSubject} growthSubject={growthSubject} data-id="element-9036" data-mp="foccq27e5zbe" />
          

          {/* Latest Assessment Result (Compact Chip) */}
          <section data-id="element-9037" data-mp="tgshb7fxy5ef">
            {isAssessmentLoading ? <Card className="p-4 border-slate-200 shadow-sm bg-white flex items-center justify-between" data-id="element-9038" data-mp="tfqu64lnhsgc">
                <div className="flex items-center gap-3" data-id="element-9039" data-mp="ye24egfuy4z3">
                  <Skeleton className="w-8 h-8 rounded-lg" data-id="element-9040" data-mp="3e69hbn0yobta" />
                  <Skeleton className="h-4 w-32" data-id="element-9041" data-mp="3e8vlrdsk2b7a" />
                </div>
                <Skeleton className="h-8 w-24 rounded-lg" data-id="element-9042" data-mp="3j7fbzqe6m95g" />
              </Card> : latestAssessmentReport ? <Card role="button" tabIndex={0} onClick={() => onNavigate?.('toetsen')} onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onNavigate?.('toetsen');
            }
          }} className="p-4 border-slate-200 shadow-sm bg-white flex items-center justify-between hover:bg-slate-50 hover:border-emerald-200 transition-all cursor-pointer group focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none" data-id="element-9043" data-mp="1pltxwcuiiqvz">
              
                <div className="flex items-center gap-3" data-id="element-9044" data-mp="11w9clnavzhjj">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-indigo-50 text-indigo-600" data-id="element-9045" data-mp="wxocrozjqzvm">
                    <FileText className="w-4 h-4" data-id="element-9046" data-mp="s0xz18b7sgdd" />
                  </div>
                  <div data-id="element-9047" data-mp="2rbfu7ao4ao1m">
                    <div className="text-sm font-bold text-slate-900" data-id="element-9048" data-mp="2rbpkz0gzkz8d">
                      Laatste toets:{' '}
                      {latestAssessmentReport.overallEstimatedLevelCode}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-9049" data-mp="2tuuactpdll2g">
                      {new Date(latestAssessmentReport.completedAt).toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm font-bold text-emerald-600 group-hover:text-emerald-700" data-id="element-9050" data-mp="2az47735piimz">
                  Naar Toetsen <ArrowRight className="w-4 h-4" data-id="element-9051" data-mp="2fz8f44sju51l" />
                </div>
              </Card> : <Card role="button" tabIndex={0} onClick={() => onNavigate?.('toetsen')} onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onNavigate?.('toetsen');
            }
          }} className="p-4 border border-dashed border-slate-200 bg-slate-50/40 flex items-center justify-between hover:bg-slate-50 hover:border-emerald-200 transition-all cursor-pointer group focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none" data-id="element-9052" data-mp="2kunresawrsvw">
              
                <div className="flex items-center gap-3" data-id="element-9053" data-mp="colv5tw1tlrv">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-400" data-id="element-9054" data-mp="cq6rnp1xbesd">
                    <FileText className="w-4 h-4" data-id="element-9055" data-mp="7rvfh7o5os6z" />
                  </div>
                  <div className="text-sm font-bold text-slate-500" data-id="element-9056" data-mp="lnh2586x2zw8">
                    Nog geen toets gemaakt
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm font-bold text-slate-400 group-hover:text-emerald-600" data-id="element-9057" data-mp="vfmvomcthpdj">
                  Naar Toetsen <ArrowRight className="w-4 h-4" data-id="element-9058" data-mp="qgjzlretethp" />
                </div>
              </Card>}
          </section>

          {/* All Modules List */}
          <section data-id="element-9059" data-mp="10eg7898jn6b2">
            <div className="flex items-center justify-between mb-4" data-id="element-9060" data-mp="10ezemw0kicau">
              <h3 className="text-lg font-black text-slate-900" data-id="element-9061" data-mp="1jzzwjwz6k8b2">
                Alle geoefende vakken
              </h3>
              <select value={sortBy} onChange={e => setSortBy(e.target.value as SortOption)} className="text-sm border-slate-200 rounded-lg bg-white text-slate-700 font-medium focus:ring-emerald-500 focus:border-emerald-500 py-1.5 pl-3 pr-8 shadow-sm" data-id="element-9062" data-mp="1o23acs9bnozi">
                
                <option value="attention" data-id="element-9063" data-mp="1f2pxdplzoia1">Aandacht eerst</option>
                <option value="strong" data-id="element-9064" data-mp="10aft721v3saf">Sterk eerst</option>
                <option value="alpha" data-id="element-9065" data-mp="1538x5npiawlg">Alfabetisch</option>
              </select>
            </div>

            <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-9066" data-mp="rbub180ayfa6">
              {sortedModules.map(m => {
              const IconComp = getModuleIcon(m.module.icon);
              const growthEntry = growthData.modules.find(gm => gm.module.id === m.module.id);
              const trend = growthEntry?.trend || 'stable';
              const delta = growthEntry?.delta || 0;
              const isExpanded = selectedModuleId === m.module.id;
              return <SubjectModuleRow key={m.module.id} variant="parent" moduleId={m.module.id} moduleTitle={m.module.title} moduleIcon={<IconComp className="w-5 h-5" data-id="element-9068" data-mp="3pn752amnfbeg" />} mastery={m.mastery} trend={trend} trendDelta={delta} isExpanded={isExpanded} onToggle={() => setSelectedModuleId(isExpanded ? null : m.module.id)} data-id="element-9067" data-mp="39flvnnczrxq">
                    
                    <ParentModuleExpandContent module={m.module} childData={childData} selectedGroup={selectedGroup} mastery={m.mastery} selectedChild={selectedChild} data-id="element-9069" data-mp="m6qit1liq9a0" />
                    
                  </SubjectModuleRow>;
            })}
            </Card>
          </section>
        </div>
      </div>
    </PageLayout>;
}